import {
  type Schema,
  type ForeignKeySchemaType,
  type InterfaceSchema,
  type LiteralSchema,
  type ObjectPropertySchema,
  type ObjectSchema,
  type IntersectionSchema,
  type MappedObjectSchema,
  type TypeReferenceSchema,
  type TypeIdentifierSchema,
  type ArraySchema,
  type PrimaryKeyTableSchema,
  type UnionSchema,
  isForeignKeySchemaType,
} from "common/data/schema";
import { SchemaVisitorBase } from "common/data/schemaVisitorBase";
import { DocSchemaLibrary } from "schemas-common/docSchema";
import {
  collectUnionLiterals,
  detectDiscriminator,
  filterUnionMembers,
  resolveIdentifier,
} from "../schema/docSchemaUnionTools";

const EMPTY_PATH_PARTS: readonly string[] = [];

function toPathParts(fieldPath: string): readonly string[] {
  return fieldPath === "" ? EMPTY_PATH_PARTS : fieldPath.split(".");
}

/**
 * Describes a single foreign key field within an entity schema.
 * Pre-computed once per schema and reused across all entities of that type.
 */
export interface FkFieldDescriptor {
  readonly fieldPath: string;
  /**
   * Pre-split {@link fieldPath} segments for allocation-free field reads at extract time.
   * Empty path yields an empty array (the container value itself is the FK).
   */
  readonly fieldPathParts: readonly string[];
  readonly fkKind: ForeignKeySchemaType;
  readonly referencedEntityTypes: readonly string[];
  /**
   * For FK_TABLE and array fields whose value/element type is a discriminated
   * union: groups of nested descriptors keyed by the discriminator value.
   * At runtime, only the matching group is applied to each value/element.
   */
  readonly nestedValueGroups?: readonly NestedValueDescriptorGroup[];
  /**
   * Literal values from the same union that take priority over the FK branch.
   * When the field value matches one of these, FK extraction is skipped
   * (the SchemaValidator would match the literal branch first).
   */
  readonly literalExcludes?: ReadonlySet<string | number | boolean>;
}

function createFkFieldDescriptor(descriptor: Omit<FkFieldDescriptor, "fieldPathParts">): FkFieldDescriptor {
  return {
    ...descriptor,
    fieldPathParts: toPathParts(descriptor.fieldPath),
  };
}

/**
 * A group of FK descriptors that apply to values matching a specific
 * discriminator condition. When no discriminator is set, the descriptors
 * apply to all values (undiscriminated union or non-union value types).
 */
export interface NestedValueDescriptorGroup {
  readonly discriminator?: { readonly field: string; readonly value: string | number | boolean };
  readonly descriptors: readonly FkFieldDescriptor[];
}

function collectNestedGroups(schema: Schema): NestedValueDescriptorGroup[] {
  const resolved = resolveIdentifier(schema);

  if (resolved.kind === "UNION") {
    const unionTypes = (resolved as any).unionTypes as Schema[];
    const result = detectDiscriminator(unionTypes);

    if (result) {
      const groups: NestedValueDescriptorGroup[] = [];

      for (const branch of result.branches) {
        const litField = branch.fields[result.field];
        if (litField?.kind !== "LITERAL") {
          continue;
        }

        const branchCollector = new NestedFkDescriptorCollector(new Set());
        branchCollector.visit(branch.branchSchema);

        if (branchCollector.descriptors.length > 0) {
          groups.push({
            discriminator: { field: result.field, value: (litField as LiteralSchema).value },
            descriptors: branchCollector.descriptors,
          });
        }
      }

      return groups;
    }
  }

  const collector = new NestedFkDescriptorCollector(new Set());
  collector.visit(resolved);

  if (collector.descriptors.length === 0) {
    return [];
  }

  return [{ descriptors: collector.descriptors }];
}

/**
 * Cache from Schema object identity to pre-computed FK field descriptors.
 */
const descriptorCache = new Map<Schema, FkFieldDescriptor[]>();

/**
 * Returns the pre-computed FK field descriptors for a given schema.
 * Results are cached by schema identity so the visitor only runs once per schema.
 */
export function getFkFieldDescriptors(schema: Schema): FkFieldDescriptor[] {
  const cached = descriptorCache.get(schema);
  if (cached) {
    return cached;
  }

  const collector = new FkFieldDescriptorCollector();
  const toVisit = resolveIdentifier(schema);
  collector.visit(toVisit);

  const descriptors = collector.descriptors;
  descriptorCache.set(schema, descriptors);
  return descriptors;
}

/**
 * Shared base class for FK descriptor collectors. Provides common path tracking,
 * table descriptor creation, and visitor overrides shared by both the top-level
 * and nested collectors.
 */
abstract class FkDescriptorCollectorBase extends SchemaVisitorBase {
  public readonly descriptors: FkFieldDescriptor[] = [];
  protected readonly pathStack: string[] = [];

  protected currentPath(): string {
    return this.pathStack.join(".");
  }

  protected pushTableDescriptor(
    schema: { readonly type: string | string[]; readonly valueType: Schema },
    fkKind: FkFieldDescriptor["fkKind"],
    literalExcludes?: ReadonlySet<string | number | boolean>,
  ): void {
    const nestedValueGroups = collectNestedGroups(schema.valueType);
    this.descriptors.push(
      createFkFieldDescriptor({
        fieldPath: this.currentPath(),
        fkKind,
        referencedEntityTypes: Array.isArray(schema.type) ? schema.type : [schema.type],
        ...(nestedValueGroups.length > 0 ? { nestedValueGroups } : {}),
        ...(literalExcludes ? { literalExcludes } : {}),
      }),
    );
  }

  override visitInterface(schema: InterfaceSchema): void {
    const fields = schema.fields;
    for (const name in fields) {
      this.pathStack.push(name);
      this.visit(fields[name]);
      this.pathStack.pop();
    }
  }

  override visitObjectProperty(schema: ObjectPropertySchema): void {
    const name = schema.name;
    this.pathStack.push(name);
    this.visit(schema.schema);
    this.pathStack.pop();
  }

  override visitObject(schema: ObjectSchema): void {
    this.visit(schema.valueType);
  }

  protected visitObjectAsDynamicKeyTable(schema: ObjectSchema): boolean {
    if (this.pathStack.length === 0 || !schema.name) {
      return false;
    }

    const nestedGroups = collectNestedGroups(schema.valueType);
    if (nestedGroups.length === 0) {
      return false;
    }

    this.descriptors.push(
      createFkFieldDescriptor({
        fieldPath: this.currentPath(),
        fkKind: "FOREIGN_KEY_TABLE",
        referencedEntityTypes: [],
        nestedValueGroups: nestedGroups,
      }),
    );
    return true;
  }

  override visitIntersection(schema: IntersectionSchema): void {
    for (const member of schema.intersectionTypes) {
      this.visit(member);
    }
  }

  override visitMappedObject(schema: MappedObjectSchema): void {
    this.visit(schema.valueType);
  }

  override visitTypeReference(schema: TypeReferenceSchema): void {
    this.visit(schema.targetType);
    const typeArgs = schema.typeArgs;
    if (typeArgs) {
      for (const arg of Object.values(typeArgs)) {
        this.visit(arg);
      }
    }
  }

  override visitTypeIdentifier(schema: TypeIdentifierSchema): void {
    const referenced = DocSchemaLibrary[schema.name];
    if (referenced) {
      this.visit(referenced);
    }
  }
}

/**
 * Schema visitor that collects FK field descriptors by traversing
 * the schema tree once. Tracks field paths through interface/object
 * property nesting and records descriptors for each FK schema node found.
 */
class FkFieldDescriptorCollector extends FkDescriptorCollectorBase {
  private readonly seen = new Set<Schema>();
  private currentUnionLiterals: ReadonlySet<string | number | boolean> | undefined;

  override visit(schema: Schema): void {
    // FK leaf nodes: always record at the current path regardless of `seen`.
    // These schemas can be shared across many fields and each path needs its
    // own descriptor.
    switch (schema.kind) {
      case "FOREIGN_KEY_STRING": {
        if (this.pathStack.length > 0) {
          this.descriptors.push(
            createFkFieldDescriptor({
              fieldPath: this.currentPath(),
              fkKind: "FOREIGN_KEY_STRING",
              referencedEntityTypes: schema.allowedTypes ?? [],
            }),
          );
        }
        return;
      }
      case "FOREIGN_KEY_PATH":
      case "FOREIGN_KEY": {
        if (this.pathStack.length > 0) {
          this.descriptors.push(
            createFkFieldDescriptor({
              fieldPath: this.currentPath(),
              fkKind: schema.kind,
              referencedEntityTypes: schema.type,
              ...(this.currentUnionLiterals ? { literalExcludes: this.currentUnionLiterals } : {}),
            }),
          );
        }
        return;
      }
      case "FOREIGN_KEY_TABLE": {
        if (this.pathStack.length > 0) {
          this.pushTableDescriptor(schema, "FOREIGN_KEY_TABLE", this.currentUnionLiterals);
        } else {
          this.visit(schema.valueType);
        }
        return;
      }
      case "FOREIGN_KEY_HASH_TABLE":
      case "FOREIGN_KEY_HASH_JOIN_TABLE": {
        if (this.pathStack.length > 0) {
          this.pushTableDescriptor(schema, schema.kind, this.currentUnionLiterals);
        } else {
          this.visit(schema.valueType);
        }
        return;
      }
      default:
        break;
    }

    // For structural schemas, use `seen` only for recursion prevention.
    // After fully visiting the subtree, remove from `seen` so the same
    // schema can be re-visited from a different field path.
    if (this.seen.has(schema)) {
      return;
    }
    this.seen.add(schema);
    super.visit(schema);
    this.seen.delete(schema);
  }

  override visitArray(schema: ArraySchema): void {
    // Arrays have dynamic indices; FK fields inside elements need
    // per-element iteration via nested groups. Do not also visit arrayType
    // from the parent path — that would emit dead descriptors like
    // `items.fooId` that cannot be read against an array value.
    if (this.pathStack.length > 0) {
      const nestedGroups = collectNestedGroups(schema.arrayType);
      if (nestedGroups.length > 0) {
        this.descriptors.push(
          createFkFieldDescriptor({
            fieldPath: this.currentPath(),
            fkKind: "FOREIGN_KEY",
            referencedEntityTypes: [],
            nestedValueGroups: nestedGroups,
          }),
        );
      }
      return;
    }
    this.visit(schema.arrayType);
  }

  override visitPrimaryKeyTable(schema: PrimaryKeyTableSchema): void {
    // Primary key tables have dynamic keys that are NOT foreign references.
    // FK fields inside the table's values need per-entry iteration via nested groups,
    // just like arrays and FK_TABLEs.
    if (this.pathStack.length > 0) {
      const valueSchema = schema.valueType;
      const nestedGroups = collectNestedGroups(valueSchema);
      if (nestedGroups.length > 0) {
        this.descriptors.push(
          createFkFieldDescriptor({
            fieldPath: this.currentPath(),
            fkKind: "FOREIGN_KEY_TABLE",
            referencedEntityTypes: [],
            nestedValueGroups: nestedGroups,
          }),
        );
      }
    }
  }

  override visitMappedObject(schema: MappedObjectSchema): void {
    // Mapped objects have dynamic keys, so nested FK fields inside values
    // must be evaluated per-entry like other table-like containers.
    const nestedGroups = collectNestedGroups(schema.valueType);
    if (nestedGroups.length > 0) {
      this.descriptors.push(
        createFkFieldDescriptor({
          fieldPath: this.currentPath(),
          fkKind: "FOREIGN_KEY_TABLE",
          referencedEntityTypes: [],
          nestedValueGroups: nestedGroups,
        }),
      );
      return;
    }

    this.visit(schema.valueType);
  }

  override visitObject(schema: ObjectSchema): void {
    // Doc schema uses obj(valueType, "Table") for dynamic-key tables (Record<string, T>).
    // FK fields live on table values, not on the parent entity — iterate entries like FK_TABLE.
    if (this.visitObjectAsDynamicKeyTable(schema)) {
      return;
    }

    super.visitObject(schema);
  }

  override visitUnion(schema: UnionSchema): void {
    const allMembers = schema.unionTypes;
    const members = filterUnionMembers(allMembers);
    const literals = collectUnionLiterals(allMembers);
    const prevLiterals = this.currentUnionLiterals;

    for (const member of members) {
      const resolved = resolveIdentifier(member);
      // Only propagate literalExcludes to direct FK children of this union.
      // Non-FK members (e.g. ParameterRef interface) and their nested FK
      // fields must not inherit the union's literal excludes.
      if (literals && isForeignKeySchemaType(resolved.kind)) {
        this.currentUnionLiterals = literals;
      } else {
        this.currentUnionLiterals = undefined;
      }
      this.visit(member);
    }

    this.currentUnionLiterals = prevLiterals;
  }
}

/**
 * A sub-collector for gathering FK descriptors within a container's
 * value type. Starts with a fresh path stack but shares cycle-prevention.
 * Unlike the top-level collector, an empty path is valid here — it means
 * the container value itself is an FK ID string.
 */
class NestedFkDescriptorCollector extends FkDescriptorCollectorBase {
  constructor(private readonly seen: Set<Schema>) {
    super();
  }

  override visit(schema: Schema): void {
    if (schema.kind === "FOREIGN_KEY_STRING") {
      this.descriptors.push(
        createFkFieldDescriptor({
          fieldPath: this.currentPath(),
          fkKind: "FOREIGN_KEY_STRING",
          referencedEntityTypes: schema.allowedTypes ?? [],
        }),
      );
      return;
    }

    if (this.seen.has(schema)) {
      return;
    }
    this.seen.add(schema);

    switch (schema.kind) {
      case "FOREIGN_KEY_PATH":
      case "FOREIGN_KEY": {
        this.descriptors.push(
          createFkFieldDescriptor({
            fieldPath: this.currentPath(),
            fkKind: schema.kind,
            referencedEntityTypes: schema.type,
          }),
        );
        return;
      }
      case "FOREIGN_KEY_TABLE": {
        this.pushTableDescriptor(schema, "FOREIGN_KEY_TABLE");
        return;
      }
      case "FOREIGN_KEY_HASH_TABLE":
      case "FOREIGN_KEY_HASH_JOIN_TABLE": {
        this.pushTableDescriptor(schema, schema.kind);
        return;
      }
      default:
        break;
    }

    super.visit(schema);
  }

  override visitUnion(schema: UnionSchema): void {
    const members = filterUnionMembers(schema.unionTypes);
    for (const member of members) {
      this.visit(member);
    }
  }

  override visitArray(schema: ArraySchema): void {
    // Struct-owned array fields need a nested-group descriptor so extraction can walk elements.
    if (this.pathStack.length > 0) {
      const nestedGroups = collectNestedGroups(schema.arrayType);
      if (nestedGroups.length > 0) {
        this.descriptors.push(
          createFkFieldDescriptor({
            fieldPath: this.currentPath(),
            fkKind: "FOREIGN_KEY",
            referencedEntityTypes: [],
            nestedValueGroups: nestedGroups,
          }),
        );
        return;
      }
    }
    this.visit(schema.arrayType);
  }
}
